import React from 'react';
import { 
  Pickaxe, Factory, Package, Gauge, Target, Gem, 
  ChevronRight, CheckCircle, AlertCircle, Minus
} from 'lucide-react';

/**
 * OperationsFlowMap - Visual mini-map showing the operational flow
 * Mina → Planta → Stocks → Produção (with efficiency and risk monitoring)
 * 
 * VERSÃO LIMPA - Sem percentagens quantitativas
 * Foco em leitura executiva e perguntas de gestão
 */

// Qualitative status - documentally supported
const qualitativeStatus = {
  2026: {
    'mina-acesso-2026': { 
      status: 'attention',
      question: { pt: 'Acesso ao minério suficiente?', en: 'Sufficient ore access?' }
    },
    'planta-recuperacao-2026': { 
      status: 'healthy',
      question: { pt: 'Planta a converter?', en: 'Plant converting?' }
    },
    'stocks-minerio-2026': { 
      status: 'healthy',
      question: { pt: 'Stock protege continuidade?', en: 'Stock protects continuity?' }
    },
    'producao-receita-2026': { 
      status: 'attention',
      question: { pt: 'Base física da receita?', en: 'Physical revenue base?' }
    },
    'eficiencia-operacional-2026': { 
      status: 'attention',
      question: { pt: 'Activos produtivos?', en: 'Productive assets?' }
    },
    'risco-entrega-2026': { 
      status: 'attention',
      question: { pt: 'Plano executado?', en: 'Plan delivered?' }
    }
  },
  2025: {
    'mina-acesso-2025': { status: 'healthy', question: { pt: 'Acesso ao minério suficiente?', en: 'Sufficient ore access?' } },
    'planta-recuperacao-2025': { status: 'healthy', question: { pt: 'Planta a converter?', en: 'Plant converting?' } },
    'stocks-minerio-2025': { status: 'healthy', question: { pt: 'Stock protege continuidade?', en: 'Stock protects continuity?' } },
    'producao-receita-2025': { status: 'healthy', question: { pt: 'Base física da receita?', en: 'Physical revenue base?' } },
    'eficiencia-operacional-2025': { status: 'healthy', question: { pt: 'Activos produtivos?', en: 'Productive assets?' } },
    'risco-operacional-2025': { status: 'healthy', question: { pt: 'Plano executado?', en: 'Plan delivered?' } }
  }
};

const OperationsFlowMap = ({ activeSection, setActiveSection, year = 2026, language = 'pt' }) => {
  const is2026 = year === 2026;
  const suffix = is2026 ? '-2026' : '-2025';
  const yearData = qualitativeStatus[year] || qualitativeStatus[2026];

  const flowSteps = [
    {
      id: `mina-acesso${suffix}`,
      icon: Pickaxe,
      label: { pt: 'Mina e Acesso', en: 'Mine & Access' },
      sublabel: { pt: 'Extracção', en: 'Extraction' },
      color: 'blue'
    },
    {
      id: `planta-recuperacao${suffix}`,
      icon: Factory,
      label: { pt: 'Planta e Recuperação', en: 'Plant & Recovery' },
      sublabel: { pt: 'Tratamento', en: 'Processing' },
      color: 'emerald'
    },
    {
      id: `stocks-minerio${suffix}`,
      icon: Package,
      label: { pt: 'Stocks de Minério', en: 'Ore Stocks' },
      sublabel: { pt: 'Inventário', en: 'Inventory' },
      color: 'amber'
    },
    {
      id: `producao-receita${suffix}`,
      icon: Gem,
      label: { pt: 'Produção e Receita', en: 'Production & Revenue' },
      sublabel: { pt: 'Output', en: 'Output' },
      color: 'gold'
    }
  ];

  const supportSteps = [
    {
      id: `eficiencia-operacional${suffix}`,
      icon: Gauge,
      label: { pt: 'Eficiência', en: 'Efficiency' },
      color: 'purple'
    },
    {
      id: is2026 ? 'risco-entrega-2026' : 'risco-operacional-2025',
      icon: Target,
      label: { pt: 'Risco e Entrega', en: 'Risk & Delivery' },
      color: 'rose'
    }
  ];

  const getColorClasses = (color, isActive) => {
    const colors = {
      blue: {
        bg: isActive ? 'bg-blue-500' : 'bg-blue-500/20',
        border: isActive ? 'border-blue-400' : 'border-blue-500/30',
        text: isActive ? 'text-white' : 'text-blue-400',
        ring: 'ring-blue-400/50',
        glow: 'shadow-blue-500/30'
      },
      emerald: {
        bg: isActive ? 'bg-emerald-500' : 'bg-emerald-500/20',
        border: isActive ? 'border-emerald-400' : 'border-emerald-500/30',
        text: isActive ? 'text-white' : 'text-emerald-400',
        ring: 'ring-emerald-400/50',
        glow: 'shadow-emerald-500/30'
      },
      amber: {
        bg: isActive ? 'bg-amber-500' : 'bg-amber-500/20',
        border: isActive ? 'border-amber-400' : 'border-amber-500/30',
        text: isActive ? 'text-white' : 'text-amber-400',
        ring: 'ring-amber-400/50',
        glow: 'shadow-amber-500/30'
      },
      gold: {
        bg: isActive ? 'bg-[#C5A059]' : 'bg-[#C5A059]/20',
        border: isActive ? 'border-[#C5A059]' : 'border-[#C5A059]/30',
        text: isActive ? 'text-white' : 'text-[#C5A059]',
        ring: 'ring-[#C5A059]/50',
        glow: 'shadow-[#C5A059]/30'
      },
      purple: {
        bg: isActive ? 'bg-purple-500' : 'bg-purple-500/20',
        border: isActive ? 'border-purple-400' : 'border-purple-500/30',
        text: isActive ? 'text-white' : 'text-purple-400',
        ring: 'ring-purple-400/50',
        glow: 'shadow-purple-500/30'
      },
      rose: {
        bg: isActive ? 'bg-rose-500' : 'bg-rose-500/20',
        border: isActive ? 'border-rose-400' : 'border-rose-500/30',
        text: isActive ? 'text-white' : 'text-rose-400',
        ring: 'ring-rose-400/50',
        glow: 'shadow-rose-500/30'
      }
    };
    return colors[color] || colors.blue;
  };

  // Qualitative status indicator - minimal, clean
  const StatusDot = ({ status }) => {
    const getStatusColor = () => {
      switch (status) {
        case 'healthy':
          return 'bg-emerald-400';
        case 'attention':
          return 'bg-amber-400';
        case 'critical':
          return 'bg-red-400';
        default:
          return 'bg-slate-400';
      }
    };
    
    return (
      <div className={`w-2 h-2 rounded-full ${getStatusColor()} shadow-sm`} />
    );
  };

  const FlowNode = ({ step, isActive, onClick, showArrow = true }) => {
    const Icon = step.icon;
    const colors = getColorClasses(step.color, isActive);
    const statusData = yearData[step.id];
    
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={onClick}
          data-testid={`flow-node-${step.id}`}
          className={`
            relative flex flex-col items-center p-2 rounded-lg border-2 transition-all duration-300
            ${colors.bg} ${colors.border}
            ${isActive ? `ring-2 ${colors.ring} shadow-lg ${colors.glow}` : 'hover:scale-105'}
            cursor-pointer min-w-[90px]
          `}
          title={statusData?.question?.[language]}
        >
          <Icon className={`w-4 h-4 ${colors.text} mb-0.5`} />
          <span className={`text-[9px] font-semibold ${colors.text} text-center leading-tight`}>
            {step.label[language]}
          </span>
          
          {/* Status dot - minimal indicator */}
          {statusData && (
            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
              <StatusDot status={statusData.status} />
            </div>
          )}
          
          {/* Active indicator pulse */}
          {isActive && (
            <div className="absolute -top-1 -right-1 w-2.5 h-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
            </div>
          )}
        </button>
        
        {showArrow && (
          <div className="flex items-center text-slate-500">
            <ChevronRight className="w-3 h-3" />
          </div>
        )}
      </div>
    );
  };

  return (
    <div 
      className="bg-gradient-to-r from-[#0A192F] via-[#112240] to-[#0A192F] rounded-2xl p-4 mb-6 shadow-lg"
      data-testid="operations-flow-map"
    >
      {/* Header - Clean */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 bg-[#C5A059] rounded-full"></div>
          <h3 className="text-[#C5A059] font-semibold text-xs uppercase tracking-wider">
            {language === 'pt' ? 'Fluxo Operacional' : 'Operational Flow'}
          </h3>
          <span className="px-2 py-0.5 bg-[#C5A059]/20 text-[#C5A059] text-[10px] font-semibold rounded">
            {is2026 ? '1T 2026' : '2025'}
          </span>
        </div>
        
        {/* Legend - Minimal */}
        <div className="flex items-center gap-3 text-[9px] text-slate-500">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-emerald-400" />
            <span>{language === 'pt' ? 'Em linha' : 'On track'}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-amber-400" />
            <span>{language === 'pt' ? 'Atenção' : 'Attention'}</span>
          </div>
        </div>
      </div>

      {/* Main Flow - Clean chain */}
      <div className="flex items-center justify-center gap-1 mb-3">
        {flowSteps.map((step, index) => (
          <FlowNode
            key={step.id}
            step={step}
            isActive={activeSection === step.id}
            onClick={() => setActiveSection(step.id)}
            showArrow={index < flowSteps.length - 1}
          />
        ))}
      </div>

      {/* Support Functions Row - Simplified */}
      <div className="flex items-center justify-center gap-4 pt-3 border-t border-white/10">
        <span className="text-slate-500 text-[10px] uppercase tracking-wider">
          {language === 'pt' ? 'Monitorização' : 'Monitoring'}:
        </span>
        {supportSteps.map((step) => {
          const Icon = step.icon;
          const isActive = activeSection === step.id;
          const colors = getColorClasses(step.color, isActive);
          const statusData = yearData[step.id];
          
          return (
            <button
              key={step.id}
              onClick={() => setActiveSection(step.id)}
              data-testid={`flow-support-${step.id}`}
              className={`
                relative flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all duration-300
                ${colors.bg} ${colors.border}
                ${isActive ? `ring-1 ${colors.ring}` : 'hover:scale-105'}
                cursor-pointer
              `}
              title={statusData?.question?.[language]}
            >
              <Icon className={`w-4 h-4 ${colors.text}`} />
              <span className={`text-[10px] font-medium ${colors.text}`}>
                {step.label[language]}
              </span>
              {statusData && (
                <StatusDot status={statusData.status} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default OperationsFlowMap;
